import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Plane, Shield, FileText, MapPin, Users, 
  Star, ChevronLeft, ChevronRight, Globe, Sparkles, MessageCircle, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { getWhatsAppLink, WHATSAPP_NUMBER } from '../lib/utils';
import { useSiteServices, getServiceIcon, formatServicePrice } from '../lib/siteServices';

const HERO_BG = "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074";
const DESTINATIONS_BG = "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2021";


const destinations = [
  { name: 'Espace Schengen', code: 'schengen', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=600' },
  { name: 'Canada', code: 'canada', image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?q=80&w=600' },
  { name: 'USA', code: 'usa', image: 'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?q=80&w=600' },
  { name: 'Dubaï', code: 'dubai', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=600' },
  { name: 'Afrique du Sud', code: 'south_africa', image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?q=80&w=600' },
  { name: 'Inde', code: 'india', image: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?q=80&w=600' }
];

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const HomePage = () => {
  const { services } = useSiteServices();
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showBilletterieModal, setShowBilletterieModal] = useState(false);
  const [billetForm, setBilletForm] = useState({
    full_name: '', phone: '', departure: '', destination: '',
    departure_date: '', return_date: '', passengers: '1',
    trip_type: 'aller-retour', cabin_class: 'economique', hotel_needed: 'non', notes: ''
  });

  const handleBilletterieSubmit = (e) => {
    e.preventDefault();
    const msg = [
      `*Demande de Réservation Vol & Hôtel*`,
      ``,
      `Nom: ${billetForm.full_name}`,
      `Tél: ${billetForm.phone}`,
      `Départ: ${billetForm.departure}`,
      `Destination: ${billetForm.destination}`,
      `Date départ: ${billetForm.departure_date}`,
      billetForm.trip_type === 'aller-retour' ? `Date retour: ${billetForm.return_date}` : `Type: Aller simple`,
      `Passagers: ${billetForm.passengers}`,
      `Classe: ${billetForm.cabin_class}`,
      `Hôtel: ${billetForm.hotel_needed === 'oui' ? 'Oui' : 'Non'}`,
      billetForm.notes ? `Notes: ${billetForm.notes}` : ''
    ].filter(Boolean).join('\n');

    window.open(getWhatsAppLink(WHATSAPP_NUMBER, msg), '_blank');
    setShowBilletterieModal(false);
    setBilletForm({
      full_name: '', phone: '', departure: '', destination: '',
      departure_date: '', return_date: '', passengers: '1',
      trip_type: 'aller-retour', cabin_class: 'economique', hotel_needed: 'non', notes: ''
    });
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/testimonials');
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  return (
    <div className="bg-[#020617] overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[auto] sm:min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl hidden sm:block" />
        <div className="absolute bottom-1/3 left-1/3 w-64 h-64 bg-sky-500/5 rounded-full blur-3xl hidden sm:block" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-4 sm:pt-12 pb-28 sm:pb-32">
          <motion.div 
            className="max-w-3xl mx-auto md:mx-0 text-center md:text-left"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center justify-center md:justify-start gap-2 mb-4">
              <div className="h-px w-8 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs md:text-sm font-medium tracking-[0.1em] md:tracking-[0.2em] uppercase hero-subtitle">
                Depuis 2011 — Kinshasa & Dubaï
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 md:mb-8 hero-title"
            >
              Votre Passeport
              <br />
              <span className="text-[#D4AF37] italic">vers l'Excellence</span>
            </motion.h1>
            
            <motion.div variants={fadeInUp} className="flex justify-center md:justify-start gap-3">
              <Link to="/visa" data-testid="hero-visa-btn">
                <button className="btn-gold px-5 py-2.5 md:px-10 md:py-5 text-xs md:text-lg flex items-center gap-2">
                  Demander un Visa
                  <ArrowRight className="w-3 h-3 md:w-5 md:h-5" />
                </button>
              </Link>
              <button 
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-glass px-5 py-2.5 md:px-10 md:py-5 text-xs md:text-lg text-white"
                data-testid="hero-services-btn"
              >
                Nos Services
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="glass border-t border-white/10">
            <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 sm:py-4 md:py-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-16">
                {[
                  { value: '13+', label: "Années d'Excellence" },
                  { value: '10K+', label: 'Clients Satisfaits' },
                  { value: '50+', label: 'Destinations' },
                  { value: '98%', label: 'Taux de Réussite' }
                ].map((stat, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-xl sm:text-2xl md:text-5xl font-bold text-[#D4AF37] mb-0.5 sm:mb-1">{stat.value}</div>
                    <div className="text-[#64748B] text-[9px] sm:text-[10px] md:text-sm uppercase tracking-wider">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 relative" data-testid="services-section">
        <div className="absolute inset-0 gradient-radial opacity-50" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative">
          <motion.div 
            className="text-center mb-8 md:mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4AF37]" />
              <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-[#D4AF37]" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#D4AF37]" />
            </div>
            <h2 className="text-xl md:text-4xl font-bold text-white mb-2">
              Nos <span className="text-[#D4AF37] italic">Services</span>
            </h2>
            <p className="text-[#94A3B8] text-xs md:text-lg max-w-2xl mx-auto">
              Services premium pour tous vos projets internationaux
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
            {services.map((service, index) => {
              const Icon = getServiceIcon(service.icon);
              const price = formatServicePrice(service);
              const CardInner = () => (
                <div className="card-luxury p-3 md:p-5 h-full text-center">
                  <div className="w-8 h-8 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-[#D4AF37]/20 transition-colors">
                    <Icon className="w-4 h-4 md:w-6 md:h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xs md:text-base font-semibold text-white mb-0.5 md:mb-1 leading-tight group-hover:text-[#D4AF37] transition-colors">{service.title}</h3>
                  <p className="text-[#94A3B8] text-[10px] md:text-[13px] leading-snug line-clamp-3">{service.description}</p>
                  {service.features?.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-left inline-block" data-testid={`home-service-features-${service.id}`}>
                      {service.features.map((f, i) => (
                        <li key={i} className={`flex items-start gap-1.5 text-[10px] md:text-xs text-[#CBD5E1] ${i > 1 ? 'hidden md:flex' : ''}`}>
                          <Check className="w-3 h-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {price && <p className="text-[#D4AF37] text-xs md:text-sm font-semibold mt-1" data-testid={`home-service-price-${service.id}`}>{price}</p>}
                </div>
              );

              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  {service.action === 'billetterie' ? (
                    <button onClick={() => setShowBilletterieModal(true)} className="block group w-full text-left" data-testid={`service-card-${index}`}>
                      <CardInner />
                    </button>
                  ) : service.action === 'contact' || !service.href ? (
                    <a href={getWhatsAppLink(WHATSAPP_NUMBER, `Bonjour, je suis intéressé(e) par: ${service.title}`)} target="_blank" rel="noopener noreferrer" className="block group" data-testid={`service-card-${index}`}>
                      <CardInner />
                    </a>
                  ) : (
                    <Link to={service.href} className="block group" data-testid={`service-card-${index}`}>
                      <CardInner />
                    </Link>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section className="py-16 relative overflow-hidden" data-testid="destinations-section">
        <div className="absolute inset-0">
          <img src={DESTINATIONS_BG} alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-transparent to-[#020617]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative">
          <motion.div 
            className="flex flex-col md:flex-row md:items-end justify-between mb-6 md:mb-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 md:w-5 md:h-5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-[10px] md:text-sm font-medium tracking-widest uppercase">Destinations Visa</span>
              </div>
              <h2 className="text-lg md:text-4xl font-bold text-white whitespace-nowrap">
                Où souhaitez-vous <span className="text-[#D4AF37] italic">voyager ?</span>
              </h2>
            </div>
            <Link to="/visa" className="mt-4 md:mt-0 flex-shrink-0" data-testid="all-destinations-btn">
              <button className="btn-glass px-3 py-2 md:px-6 md:py-3 text-[10px] md:text-sm text-white flex items-center gap-2 whitespace-nowrap">
                Toutes les destinations
                <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
              </button>
            </Link>
          </motion.div>

          <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {destinations.map((dest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <Link to={`/visa/${dest.code}`} className="block group" data-testid={`dest-${dest.code}`}>
                  <div className="aspect-[3/4] rounded-xl md:rounded-2xl overflow-hidden relative">
                    <img 
                      src={dest.image} 
                      alt={dest.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute inset-0 border border-white/10 rounded-xl md:rounded-2xl group-hover:border-[#D4AF37]/50 transition-colors" />
                    <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4">
                      <p className="text-white font-semibold text-xs md:text-lg">{dest.name}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-10 md:py-16 relative" data-testid="testimonials-section">
          <div className="absolute inset-0 gradient-radial opacity-30" />
          
          <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 relative">
            <motion.div 
              className="text-center mb-6 md:mb-10"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl md:text-4xl font-bold text-white mb-2">
                Ce que disent nos <span className="text-[#D4AF37] italic">clients</span>
              </h2>
            </motion.div>

            <motion.div 
              className="glass-gold rounded-2xl p-6 md:p-12 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 md:w-5 md:h-5 text-[#D4AF37] fill-[#D4AF37]" />
                ))}
              </div>
              <p className="text-sm md:text-xl text-white font-light italic leading-relaxed mb-4 md:mb-6">
                "{testimonials[currentTestimonial]?.content}"
              </p>
              <p className="text-[#D4AF37] font-semibold text-sm md:text-base">
                {testimonials[currentTestimonial]?.client_name}
              </p>

              {testimonials.length > 1 && (
                <div className="flex justify-center items-center gap-4 mt-6">
                  <button
                    onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                    className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-white/20 hover:border-[#D4AF37] flex items-center justify-center transition-colors"
                    data-testid="prev-testimonial"
                  >
                    <ChevronLeft className="w-4 h-4 text-white" />
                  </button>
                  <div className="flex gap-2">
                    {testimonials.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentTestimonial ? 'bg-[#D4AF37]' : 'bg-white/30'
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                    className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-white/20 hover:border-[#D4AF37] flex items-center justify-center transition-colors"
                    data-testid="next-testimonial"
                  >
                    <ChevronRight className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-10 md:py-16 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/10 via-transparent to-[#D4AF37]/10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl md:text-4xl font-bold text-white mb-4">
              Prêt à <span className="text-[#D4AF37] italic">voyager</span> ?
            </h2>
            <p className="text-sm md:text-lg text-[#94A3B8] mb-8 max-w-2xl mx-auto">
              Contactez-nous dès maintenant pour bénéficier de notre expertise
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="https://wa.me/243975608422" target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp">
                <button className="btn-gold px-6 py-3 md:px-10 md:py-4 text-sm md:text-base flex items-center gap-2">
                  WhatsApp
                  <ArrowRight className="w-4 h-4" />
                </button>
              </a>
              <Link to="/contact" data-testid="cta-contact">
                <button className="btn-glass px-6 py-3 md:px-10 md:py-4 text-sm md:text-base text-white">
                  Contact
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Billetterie Modal */}
      <Dialog open={showBilletterieModal} onOpenChange={setShowBilletterieModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white text-sm">
              <Plane className="w-4 h-4 text-[#D4AF37]" />
              Réservation Vol & Hôtel
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleBilletterieSubmit} className="space-y-2.5" data-testid="billetterie-form">
            <div>
              <Label className="text-[#94A3B8] text-xs">Nom complet *</Label>
              <Input value={billetForm.full_name} onChange={(e) => setBilletForm({...billetForm, full_name: e.target.value})}
                required placeholder="Votre nom" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-name" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
              <Input value={billetForm.phone} onChange={(e) => setBilletForm({...billetForm, phone: e.target.value})}
                required placeholder="+243..." className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-phone" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#94A3B8] text-xs">Ville de départ *</Label>
                <Input value={billetForm.departure} onChange={(e) => setBilletForm({...billetForm, departure: e.target.value})}
                  required placeholder="Ex: Kinshasa" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-departure" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Destination *</Label>
                <Input value={billetForm.destination} onChange={(e) => setBilletForm({...billetForm, destination: e.target.value})}
                  required placeholder="Ex: Paris" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-destination" />
              </div>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Type de voyage</Label>
              <Select value={billetForm.trip_type} onValueChange={(v) => setBilletForm({...billetForm, trip_type: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-trip-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10">
                  <SelectItem value="aller-retour" className="text-xs">Aller-Retour</SelectItem>
                  <SelectItem value="aller-simple" className="text-xs">Aller Simple</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#94A3B8] text-xs">Date départ *</Label>
                <Input type="date" value={billetForm.departure_date} onChange={(e) => setBilletForm({...billetForm, departure_date: e.target.value})}
                  required className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-dep-date" />
              </div>
              {billetForm.trip_type === 'aller-retour' && (
                <div>
                  <Label className="text-[#94A3B8] text-xs">Date retour</Label>
                  <Input type="date" value={billetForm.return_date} onChange={(e) => setBilletForm({...billetForm, return_date: e.target.value})}
                    className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-ret-date" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#94A3B8] text-xs">Passagers</Label>
                <Select value={billetForm.passengers} onValueChange={(v) => setBilletForm({...billetForm, passengers: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-passengers">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <SelectItem key={n} value={String(n)} className="text-xs">{n} passager{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Classe</Label>
                <Select value={billetForm.cabin_class} onValueChange={(v) => setBilletForm({...billetForm, cabin_class: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-class">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    <SelectItem value="economique" className="text-xs">Économique</SelectItem>
                    <SelectItem value="premium" className="text-xs">Premium Economy</SelectItem>
                    <SelectItem value="affaires" className="text-xs">Affaires</SelectItem>
                    <SelectItem value="premiere" className="text-xs">Première</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Réservation hôtel ?</Label>
              <Select value={billetForm.hotel_needed} onValueChange={(v) => setBilletForm({...billetForm, hotel_needed: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-hotel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10">
                  <SelectItem value="non" className="text-xs">Non</SelectItem>
                  <SelectItem value="oui" className="text-xs">Oui, j'ai besoin d'un hôtel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Notes (optionnel)</Label>
              <Input value={billetForm.notes} onChange={(e) => setBilletForm({...billetForm, notes: e.target.value})}
                placeholder="Préférences, escales..." className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="billet-notes" />
            </div>

            <button type="submit" className="w-full btn-gold py-2.5 text-xs flex items-center justify-center gap-2" data-testid="billet-submit">
              <MessageCircle className="w-3.5 h-3.5" />
              Envoyer via WhatsApp
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
